/**
 * clientProjects.ts — Phase-2 Slice 3: LIVE, per-client project + money source.
 *
 * Until this slice the client surfaces (Home · Journey · Report) read the
 * DEMO seed (`trackDemo.ts` DEMO_PROJECTS / seedProjects) — fabricated rows,
 * never the signed-in client's real data. This module replaces that with a
 * LIVE Firestore listener scoped to ONE client:
 *
 *   listenClientProjects(clientId, cb)
 *     → streams `projects where clientId == <clientId>` (the firestore.rules
 *       client-read clause permits exactly this query — a client may read a
 *       project doc iff `resource.data.clientId == token.contactId`). A client
 *       therefore sees ONLY their own projects; another client's projects are
 *       never returned (and a rules-denied query surfaces via onErr).
 *
 * MONEY (owner-locked: money IS client-visible) is computed from the
 * AUTHORITATIVE per-project `gateState.moneyPaid` map — the SOLE money
 * authority (memory project_money_before_delivery, 2026-06-04). We do NOT read
 * the cached `clearedGateIds` field (it can drift); we read which money gates
 * are actually cleared in `gateState.moneyPaid` and feed the SAME pure pricing
 * math the demo layer used (`trackDemo.moneyViewOf` / `clientProjectMoney` /
 * `clientMoneySummary`) — ONE pricing path, locked engagementTemplate gates.
 *
 * PURE/IMPURE split: the doc→DemoProject mapper + the gate-clearing derivation
 * are pure (unit-tested); `listenClientProjects` is the thin Firestore seam.
 *
 * 2026-06-04 — Anil (Phase-2 Slice 3).
 */
import {
  collection,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Tier } from './engagementTemplate';
import {
  clientMoneySummary,
  clientProjectMoney,
  type ClientMoneySummary,
  type ClientProjectMoney,
  type DemoProject,
} from './trackDemo';

/* ════════════════════ Pure mapping (doc → client project) ════════════════════ */

/**
 * Which money gate ids are CLEARED for this project, read from the
 * AUTHORITATIVE `gateState.moneyPaid` map (not the cached `clearedGateIds`
 * field). A gate id is cleared iff `gateState.moneyPaid[id] === true`. Defensive
 * against an absent/partial gateState (a fresh project has none → []).
 */
export function clearedMoneyGateIds(
  d: Record<string, unknown> | null | undefined,
): string[] {
  const gate = d && (d.gateState as Record<string, unknown> | undefined);
  const paid = gate && (gate.moneyPaid as Record<string, unknown> | undefined);
  if (!paid || typeof paid !== 'object') return [];
  return Object.keys(paid).filter((k) => paid[k] === true);
}

/**
 * Map a live `projects/{pid}` doc to the `DemoProject` SHAPE the pure money
 * math consumes. The shape is reused verbatim so we keep ONE pricing path; the
 * data is now LIVE + client-scoped. `clearedGateIds` is derived from the
 * authoritative `gateState.moneyPaid`. Returns null for a doc with no tierCode
 * (un-priced imported record — not a real engagement to show money for).
 */
export function clientProjectFromDoc(
  id: string,
  d: Record<string, unknown>,
): DemoProject | null {
  const tierCode = d.tierCode as Tier | undefined;
  if (!tierCode) return null; // un-priced / imported record — no money picture
  return {
    pid: id,
    vp: (d.vp as string) ?? id,
    projectName: typeof d.projectName === 'string' ? d.projectName.trim() : undefined,
    clientName: (d.clientName as string) ?? '—',
    clientNo: (d.clientNo as string) ?? '—',
    tier: tierCode,
    activeStageKey: (d.activeStageKey as string) ?? '',
    assigneeUid: (d.assigneeUid as string) ?? 'unassigned',
    assigneeName: (d.assigneeName as string) ?? 'Unassigned',
    valueINR: (d.valueINR as number) ?? 0,
    // AUTHORITATIVE: money cleared = the per-project gateState.moneyPaid map.
    clearedGateIds: clearedMoneyGateIds(d),
    openProposals: 0, // client never sees the review/proposal layer
  };
}

/**
 * Exclude archived / trashed projects from the client's money + journey view
 * (they are not active engagements). PURE — operates on the already-loaded
 * stream; no extra reads.
 */
export function activeClientDocs(
  docs: Array<{ id: string; d: Record<string, unknown> }>,
): DemoProject[] {
  const out: DemoProject[] = [];
  for (const { id, d } of docs) {
    if (d.archived === true) continue;
    if (d.trashed === true) continue;
    const p = clientProjectFromDoc(id, d);
    if (p) out.push(p);
  }
  return out;
}

/* ════════════════════ Live client-scoped money views ════════════════════ */

/** The per-project money + journey for a client (reuses the locked math). */
export function clientProjectMoneyLive(p: DemoProject): ClientProjectMoney {
  return clientProjectMoney(p);
}

/** Cross-project money summary for THIS client's own projects only. */
export function clientMoneySummaryLive(
  projects: DemoProject[],
): ClientMoneySummary {
  return clientMoneySummary(projects);
}

/* ════════════════════ Live listener (impure seam) ════════════════════ */

export interface ClientProjectsState {
  /** This client's active projects (un-priced/imported docs filtered out). */
  projects: DemoProject[];
  /** Cross-project money summary (paid / due / next-to-pay across all). */
  summary: ClientMoneySummary;
}

/**
 * Stream the signed-in client's OWN projects + the derived money summary.
 * `clientId` MUST be the signed-in client's id (auth.clientId / contactId) —
 * the query is `projects where clientId == clientId`, and the firestore.rules
 * client-read clause enforces `resource.data.clientId == token.contactId`, so
 * the result is provably scoped to this client. An empty/absent clientId
 * yields an empty state (a non-client session has no client projects).
 *
 * `onReady` fires once on the first snapshot (success OR error) so a caller can
 * distinguish "still loading" from "genuinely no engagement yet".
 */
export function listenClientProjects(
  clientId: string | null | undefined,
  cb: (state: ClientProjectsState) => void,
  opts?: { onErr?: (e: Error) => void; onReady?: () => void },
): Unsubscribe {
  if (!clientId) {
    cb({ projects: [], summary: clientMoneySummaryLive([]) });
    opts?.onReady?.();
    return () => {};
  }
  let readyFired = false;
  const fireReady = () => {
    if (!readyFired) {
      readyFired = true;
      opts?.onReady?.();
    }
  };
  const q = query(collection(db, 'projects'), where('clientId', '==', clientId));
  return onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, d: d.data() as Record<string, unknown> }));
      const projects = activeClientDocs(docs);
      cb({ projects, summary: clientMoneySummaryLive(projects) });
      fireReady();
    },
    (e) => {
      opts?.onErr?.(e);
      fireReady();
    },
  );
}
